import React, { useState, useEffect } from "react";
import AppointmentBooking from "./components/AppointmentBooking";
import "./style.css"; // Import the CSS file

const SprinterVanBookingForm = ({ showProgress = true }) => {
  const API_BASE_URL = process.env.REACT_APP_API_URL || "/api";
  
  // Payment control - set to false to disable payment requirement
  const [usePayment, setUsePayment] = useState(false);
  
  // State management
  const [currentStep, setCurrentStep] = useState(1);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [appointmentBooked, setAppointmentBooked] = useState(false);
  const [appointmentInfo, setAppointmentInfo] = useState(null);
  const [showThankYou, setShowThankYou] = useState(false);
  const [thankYouMessage, setThankYouMessage] = useState("");
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [emailError, setEmailError] = useState("");
  const [bookingReady, setBookingReady] = useState(false);
  const [bookingSubmitFn, setBookingSubmitFn] = useState(null);
  
  // Simplified form data - only essential fields for 2-step form
  const [formData, setFormData] = useState({
    // Step 1 - Personal Info
    first_name: "",
    last_name: "",
    email: "",
    user_type: "visitor", // Default to visitor since user type selection is removed
    no_of_people: "",
    pickup_location: "",
    dropoff_location: "",

    // Step 2 - Package & Payment
    package: "Sprinter Van Booking",
    email_optin: true,
    terms_agreement: false,
  });

  // Form step definitions - Only 2 steps now
  const FORM_STEPS = [
    { id: 1, title: "Your Sprinter Van Details", icon: "fas fa-user" },
    { id: 2, title: usePayment ? "Book & Pay for Van" : "Book Your Van", icon: "fas fa-calendar-check" },
  ];

  const [sessionId] = useState(() => {
    // Try to get existing session ID from localStorage
    const existingSessionId = localStorage.getItem("nc_session_id");
    if (existingSessionId) {
      return existingSessionId;
    }

    // Generate a new session ID if none exists
    const newSessionId = `nc_react_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;
    localStorage.setItem("nc_session_id", newSessionId);
    return newSessionId;
  });

  // Load saved form data and step from localStorage on component mount
  useEffect(() => {
    const loadSavedData = () => {
      try {
        console.log("Loading saved data for session:", sessionId);

        const savedFormData = localStorage.getItem(`nc_form_data_${sessionId}`);
        const savedStep = localStorage.getItem(`nc_current_step_${sessionId}`);

        console.log("Saved form data:", savedFormData);
        console.log("Saved step:", savedStep);

        if (savedFormData) {
          const parsedData = JSON.parse(savedFormData);
          console.log("Parsed saved data:", parsedData);
          setFormData(parsedData);
        }

        if (savedStep) {
          const stepNumber = parseInt(savedStep, 10);
          console.log("Restoring to step:", stepNumber);
          if (stepNumber > 1 && stepNumber <= FORM_STEPS.length) {
            setCurrentStep(stepNumber);
          }
        }
      } catch (error) {
        console.error("Error loading saved form data:", error);
      }
    };

    // Add a small delay to ensure localStorage is ready
    const timer = setTimeout(() => {
      loadSavedData();
    }, 100);

    return () => clearTimeout(timer);
  }, [sessionId]);

  // Save form data to localStorage
  const saveFormDataToLocalStorage = (updatedData) => {
    try {
      localStorage.setItem(
        `nc_form_data_${sessionId}`,
        JSON.stringify(updatedData)
      );
      console.log("Saved to localStorage:", updatedData);
    } catch (error) {
      console.error("Error saving form data:", error);
    }
  };

  // Save current step to localStorage
  const saveCurrentStepToLocalStorage = (step) => {
    try {
      localStorage.setItem(`nc_current_step_${sessionId}`, step.toString());
      console.log("Saved step to localStorage:", step);
    } catch (error) {
      console.error("Error saving current step:", error);
    }
  };

  // Get saved lead ID from localStorage
  const getSavedLeadId = () => {
    try {
      const leadId = localStorage.getItem(`nc_lead_id_${sessionId}`);
      return leadId ? leadId : null;
    } catch (error) {
      console.error("Error getting saved lead ID:", error);
      return null;
    }
  };

  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle form field changes
  const handleInputChange = (name, value) => {
    const updatedData = {
      ...formData,
      [name]: value,
    };
    setFormData(updatedData);
    saveFormDataToLocalStorage(updatedData);

    // Real-time email validation
    if (name === "email") {
      if (value.trim() === "") {
        setEmailError("");
      } else if (!validateEmail(value)) {
        setEmailError("Please enter a valid email address");
      } else {
        setEmailError("");
      }
    }
  };

  // Navigation functions
  const goToNextStep = async () => {
    // Prevent advancing past appointment step until booking is complete
    if (currentStep === 2 && !appointmentBooked) {
      console.warn("Cannot proceed to next step: appointment not booked yet.");
      setPaymentError(usePayment ? "Please complete the van booking and payment to continue." : "Please complete the van booking to continue.");
      return;
    }

    if (currentStep < FORM_STEPS.length && isCurrentStepValid()) {
      // Save current step data to PerfexCRM before advancing
      console.log(`Saving step ${currentStep} data to PerfexCRM:`, {
        sessionId,
        step: currentStep,
        data: getStepData(currentStep),
      });

      try {
        // Sync current form state with CRM Leads API
        const syncResult = await syncWithCRMLeads(false, currentStep);
        if (syncResult.error) {
          console.warn(
            `Step ${currentStep} CRM Leads sync warning:`,
            syncResult.error
          );
          // Don't block progression for CRM sync issues
        } else {
          console.log(
            `Step ${currentStep} synced successfully with CRM Leads API`
          );
          // Save lead ID to localStorage if provided
          if (syncResult.leadId) {
            localStorage.setItem(
              `nc_lead_id_${sessionId}`,
              syncResult.leadId.toString()
            );
            console.log(`Saved lead ID to localStorage: ${syncResult.leadId}`);
          }
        }
      } catch (error) {
        console.error(`Step ${currentStep} CRM Leads sync failed:`, error);
        // Don't block progression for CRM sync failures
      }

      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      saveCurrentStepToLocalStorage(nextStep);
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 1) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      saveCurrentStepToLocalStorage(prevStep);
    }
  };

  // Get data for a specific step
  const getStepData = (step) => {
    switch (step) {
      case 1:
        const firstName = (formData.first_name || "").trim();
        const lastName = (formData.last_name || "").trim();
        const fullName = [firstName, lastName].filter(Boolean).join(" ");
        return {
          name: fullName,
          email: formData.email,
          no_of_people: formData.no_of_people,
          pickup_location: formData.pickup_location,
          dropoff_location: formData.dropoff_location,
        };
      case 2:
        return {
          appointmentBooked: appointmentBooked,
          appointmentInfo: appointmentInfo,
          paymentComplete: paymentComplete,
          user_type: formData.user_type,
          no_of_people: formData.no_of_people,
          pickup_location: formData.pickup_location,
          dropoff_location: formData.dropoff_location,
        };
      default:
        return {};
    }
  };

  // Update step description
  const getCurrentStepDescription = () => {
    const step = FORM_STEPS.find((s) => s.id === currentStep);
    return step ? step.title : "";
  };

  // Form validation for current step
  const isCurrentStepValid = () => {
    switch (currentStep) {
      case 1:
        return (
          formData.first_name &&
          formData.first_name.trim() &&
          formData.last_name &&
          formData.last_name.trim() &&
          formData.email &&
          formData.email.trim() &&
          validateEmail(formData.email) &&
          validateEmail(formData.email) &&
          formData.no_of_people &&
          !emailError
        );
      case 2:
        // Appointment step - payment optional based on usePayment setting
        return appointmentBooked && (usePayment ? paymentComplete : true);
      default:
        return true;
    }
  };

  // Clear all localStorage data for this session
  const clearFormProgress = () => {
    try {
      localStorage.removeItem(`nc_form_data_${sessionId}`);
      localStorage.removeItem(`nc_current_step_${sessionId}`);
      localStorage.removeItem(`nc_lead_id_${sessionId}`);
      localStorage.removeItem("nc_session_id");
    } catch (error) {
      console.error("Error clearing form progress:", error);
    }
  };
  // Sync form data with CRM Leads API - simplified for 2-step form
  const syncWithCRMLeads = async (
    includePaymentStatus = false,
    currentStepOverride = null,
    customStatus = null,
    includeAllData = false
  ) => {
    try {
      const stepToSync = currentStepOverride || currentStep;
      console.log(
        `Syncing step ${stepToSync} with CRM Leads API following integration guide...`
      );

      // Prepare comprehensive form data for CRM
      const firstName = (formData.first_name || "").trim();
      const lastName = (formData.last_name || "").trim();
      const fullName = [firstName, lastName].filter(Boolean).join(" ");

      let syncData;

      if (includeAllData || stepToSync === 2) {
        // For final submission or when explicitly requested, include ALL form data
        syncData = {
          // Basic info
          name: fullName,
          first_name: firstName,
          last_name: lastName,
          email: formData.email,
          user_type: formData.user_type,

          // Package & Payment
          package: formData.package,
          email_optin: formData.email_optin,
          terms_agreement: formData.terms_agreement,

          // Appointment info
          ...(appointmentInfo && {
            appointment_date: appointmentInfo.date,
            appointment_time: appointmentInfo.time,
            appointment_timezone: appointmentInfo.timezone,
            appointment_subject: appointmentInfo.subject,
            appointment_service: appointmentInfo.service,
            appointment_provider: appointmentInfo.provider,
            appointment_id: appointmentInfo.appointmentId,
          }),

          // Essential fields
          no_of_people: formData.no_of_people,
          pickup_location: formData.pickup_location,
          dropoff_location: formData.dropoff_location,

          // Status and payment info
          ...(includePaymentStatus && { payment_status: "completed" }),
          ...(customStatus && { crm_status_override: customStatus }),
        };
      } else {
        // For step-by-step sync, send accumulated data so far
        syncData = {
          // Basic info (always include)
          name: fullName,
          first_name: firstName,
          last_name: lastName,
          email: formData.email,
          user_type: formData.user_type,

          // Package & Payment (if completed)
          ...(formData.package && { package: formData.package }),
          ...(formData.email_optin !== undefined && { email_optin: formData.email_optin }),
          ...(formData.terms_agreement !== undefined && { terms_agreement: formData.terms_agreement }),

          // Appointment info (if completed)
          ...(appointmentInfo && {
            appointment_date: appointmentInfo.date,
            appointment_time: appointmentInfo.time,
            appointment_timezone: appointmentInfo.timezone,
            appointment_subject: appointmentInfo.subject,
            appointment_service: appointmentInfo.service,
            appointment_provider: appointmentInfo.provider,
            appointment_id: appointmentInfo.appointmentId,
          }),

          // Essential fields
          ...(formData.no_of_people && { no_of_people: formData.no_of_people }),
          ...(formData.pickup_location && { pickup_location: formData.pickup_location }),
          ...(formData.dropoff_location && { dropoff_location: formData.dropoff_location }),

          // Status and payment info
          ...(includePaymentStatus && { payment_status: "completed" }),
          ...(customStatus && { crm_status_override: customStatus }),
        };
      }

      // Debug logging to see what we're sending
      console.log("🔍 Debug: Form data being processed:", {
        firstName,
        lastName,
        fullName,
        formDataFirstName: formData.first_name,
        formDataLastName: formData.last_name,
      });
      console.log("📤 Debug: Final syncData being sent to API:", syncData);

      // Use the CRM Leads integration endpoint
      const response = await fetch(`${API_BASE_URL}/crm-leads-integration`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          session_id: sessionId,
          current_step: stepToSync,
          form_data: syncData,
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(
          result.error || result.message || "Failed to sync with CRM"
        );
      }

      console.log(`CRM Leads sync successful for step ${stepToSync}:`, {
        action: result.action,
        leadId: result.leadId,
        step: result.step,
      });

      return result;
    } catch (error) {
      console.error(
        `CRM Leads sync failed for step ${currentStepOverride || currentStep}:`,
        error
      );
      // Don't fail the form submission if CRM sync fails - graceful degradation
      return { error: error.message };
    }
  };

  // Appointment handlers
  const handleAppointmentComplete = async (appointmentData) => {
    console.log("Appointment booking completed:", appointmentData);
    setAppointmentBooked(true);
    setAppointmentInfo(appointmentData);
    
    // Mark payment as complete if payment was processed or if payment is not required
    if (appointmentData.paymentComplete || !usePayment) {
      setPaymentComplete(true);
    }

    // Update lead status to 7 in CRM Leads API (appointment booked)
    try {
      const syncResult = await syncWithCRMLeads(usePayment && appointmentData.paymentComplete, 2, 7);
      if (syncResult.error) {
        console.warn(
          "CRM Leads sync warning after appointment:",
          syncResult.error
        );
      } else {
        console.log(`Lead status updated to 7 after appointment booking${usePayment ? ' with payment' : ''}`);
      }
    } catch (error) {
      console.error("Failed to update lead status after appointment:", error);
    }

    // Complete the form submission
    await handleSubmit();
  };

  // Handle final form submission
  const handleSubmit = async () => {

    if (paymentComplete) {
      console.log("Form completed with payment!");
      // Update lead status to 7 (form completed with payment) - include ALL data
      const syncResult = await syncWithCRMLeads(true, 2, 7, true); // true for includeAllData
      // Save lead ID one final time if provided
      if (syncResult && syncResult.leadId) {
        localStorage.setItem(
          `nc_lead_id_${sessionId}`,
          syncResult.leadId.toString()
        );
        console.log(
          `Final submission - Saved lead ID to localStorage: ${syncResult.leadId}`
        );
      }
      // Clear localStorage data since form is complete
      clearFormProgress();
      setThankYouMessage(
        "Thank you! Your Sprinter Van booking has been submitted and payment processed successfully. Our team will contact you soon!"
      );
      setShowThankYou(true);
      setFormSubmitted(true);
    } else {
      console.log("Form submitted with data:", formData);
      console.log("Session ID:", sessionId);
      // Update lead status to 7 (form completed without payment) - include ALL data
      const syncResult = await syncWithCRMLeads(false, 2, 7, true); // true for includeAllData
      // Save lead ID one final time if provided
      if (syncResult && syncResult.leadId) {
        localStorage.setItem(
          `nc_lead_id_${sessionId}`,
          syncResult.leadId.toString()
        );
        console.log(
          `Final submission - Saved lead ID to localStorage: ${syncResult.leadId}`
        );
      }
      // Clear localStorage data since form is complete
      clearFormProgress();
      setThankYouMessage(
        "Thank you! Your request has been submitted successfully. Our sprinter van team will be in contact with you within 24 hours to confirm and complete your booking."
      );
      setShowThankYou(true);
      setFormSubmitted(true);
    }
  };

  // Handle booking readiness from AppointmentBooking component
  const handleBookingReady = (isReady, submitFunction) => {
    setBookingReady(isReady);
    setBookingSubmitFn(() => submitFunction);
  };

  // Effect to update progress indicators
  useEffect(() => {
    // Update progress indicators active state based on current step
    // This will be handled in the component render
  }, [currentStep]);
  return (
    <div className="nc-form-container" data-session-id={sessionId}>
      {showThankYou ? (
        // Thank You Screen
        <div className="nc-thank-you-container">
          <div className="nc-thank-you-card">
            <div className="nc-thank-you-header">
              <div className="nc-thank-you-icon">
                <i className="fas fa-check-circle"></i>
              </div>
              <h1>Thank You!</h1>
              <p className="nc-thank-you-message">{thankYouMessage}</p>
            </div>

            <div className="nc-thank-you-content">
              {appointmentInfo && (
                <div className="nc-appointment-confirmation">
                  <h3>Your Booking Details</h3>
                  <div className="nc-appointment-details">
                    <div>
                      <strong>Date:</strong> {appointmentInfo.date}
                    </div>
                    <div>
                      <strong>Time:</strong> {appointmentInfo.time} Central Time
                    </div>
                    <div>
                      <strong>Service:</strong> {appointmentInfo.service}
                    </div>
                    <div>
                      <strong>Provider:</strong> {appointmentInfo.provider}
                    </div>
                  </div>
                </div>
              )}

              <div className="nc-next-steps">
                <h3>What's Next?</h3>
                <div className="nc-steps-list">
                  <div className="nc-step-item">
                    <i className="fas fa-envelope"></i>
                    <span>You'll receive a confirmation email shortly</span>
                  </div>
                  <div className="nc-step-item">
                    <i className="fas fa-phone"></i>
                    <span>Our team will contact you within 24 hours</span>
                  </div>
                  {appointmentInfo && (
                    <div className="nc-step-item">
                      <i className="fas fa-calendar-check"></i>
                      <span>Your appointment is confirmed and reserved</span>
                    </div>
                  )}
                  <div className="nc-step-item">
                    <i className="fas fa-star"></i>
                    <span>Get ready for an amazing Sprinter Van experience!</span>
                  </div>
                </div>
              </div>

              <div className="nc-contact-info">
                <h3>Need Immediate Assistance?</h3>
                <p>Contact our Sprinter Van team:</p>
                <div className="nc-contact-details">
                  <div>
                    <strong>Phone:</strong> (615) 499-9603
                  </div>
                  <div>
                    <strong>Email:</strong> service@nashvilletnconcierge.com
                  </div>
                </div>
              </div>
            </div>

            <div className="nc-thank-you-footer">
              <button
                className="nc-btn nc-btn-primary"
                onClick={() => window.location.href = "https://nashvilletnconcierge.com"}
                style={{ marginTop: "20px" }}
              >
                Return Home <i className="fas fa-home"></i>
              </button>
            </div>
          </div>
        </div>
      ) : (
        // Main Form
        <>
          {/* Main Card */}
          <div className="nc-form-card">
            {/* Progress Header */}
            {showProgress && (
              <div className="nc-progress-header">
                <div className="nc-progress-indicators">
                  {FORM_STEPS.map((step, index, filteredSteps) => (
                    <React.Fragment key={step.id}>
                      <div
                        className={`nc-step-indicator ${
                          step.id === currentStep
                            ? "active"
                            : step.id < currentStep
                            ? "completed"
                            : ""
                        }`}
                        data-step={step.id}
                      >
                        <i className={step.icon}></i>
                      </div>
                      {index < filteredSteps.length - 1 && (
                        <div
                          className={`nc-progress-line ${
                            step.id < currentStep ? "completed" : ""
                          }`}
                        ></div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
                <div className="nc-step-description" id="nc-step-description">
                  {getCurrentStepDescription()}
                </div>
              </div>
            )}

            {/* Form Container */}
            <div id="nc-multi-step-form" data-session-id={sessionId}>
              {/* Step 1: Personal Info */}
              {currentStep === 1 && (
                <div className="nc-step active" id="nc-step-1">
                  <div className="nc-step-header">
                    <div className="nc-step-icon">
                      <i className="fas fa-user"></i>
                    </div>
                    <h2>Welcome to Sprinter Van Booking!</h2>
                    <p>Let's start with your basic information</p>
                  </div>

                  <div className="nc-form-fields">
                    <div className="nc-field-row">
                      <div className="nc-field">
                        <label>First Name</label>
                        <input
                          type="text"
                          name="first_name"
                          value={formData.first_name}
                          onChange={(e) =>
                            handleInputChange("first_name", e.target.value)
                          }
                          required
                        />
                      </div>
                      <div className="nc-field">
                        <label>Last Name</label>
                        <input
                          type="text"
                          name="last_name"
                          value={formData.last_name}
                          onChange={(e) =>
                            handleInputChange("last_name", e.target.value)
                          }
                          required
                        />
                      </div>
                    </div>

                    <div className="nc-field">
                      <label>Email Address</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={(e) =>
                          handleInputChange("email", e.target.value)
                        }
                        className={emailError ? "nc-input-error" : ""}
                        required
                      />
                      {emailError && (
                        <div className="nc-field-error">{emailError}</div>
                      )}
                    </div>

                    <div className="nc-field">
                      <label>Number of People</label>
                      <select
                        name="no_of_people"
                        value={formData.no_of_people}
                        onChange={(e) =>
                          handleInputChange("no_of_people", e.target.value)
                        }
                        required
                      >
                        <option value="">Select number of people</option>
                        {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                          <option key={num} value={num}>
                            {num} {num === 1 ? "Person" : "People"}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Appointment Booking & Payment */}
              {currentStep === 2 && (
                <div className="nc-step active" id="nc-step-2">
                  <div className="nc-step-header">
                    <div className="nc-step-icon">
                      <i className="fas fa-calendar-check"></i>
                    </div>
                    <h2>Book Your Sprinter Van</h2>
                    <p>Book your luxury Sprinter van with a professional driver in 3 easy steps</p>
                  </div>

                  <div className="nc-form-fields">
                    <AppointmentBooking
                      formData={formData}
                      customerInfo={{
                        first_name: formData.first_name,
                        last_name: formData.last_name,
                        email: formData.email,
                      }}
                      sessionId={sessionId}
                      onAppointmentComplete={handleAppointmentComplete}
                      paymentError={paymentError}
                      setPaymentError={setPaymentError}
                      paymentProcessing={paymentProcessing}
                      setPaymentProcessing={setPaymentProcessing}
                      usePayment={usePayment}
                      hideButtons={true}
                      onBookingReady={handleBookingReady}
                      onInputChange={handleInputChange} 
                    />
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="nc-form-navigation">
                <button
                  type="button"
                  id="nc-back-btn"
                  className={`nc-btn nc-btn-secondary ${
                    currentStep === 1 ? "hidden" : ""
                  }`}
                  onClick={goToPreviousStep}
                >
                  <i className="fas fa-arrow-left"></i> Back
                </button>
                <div></div>
                <button
                  type="button"
                  id="nc-next-btn"
                  className="nc-btn nc-btn-primary"
                  onClick={
                    currentStep === 1
                      ? goToNextStep
                      : bookingSubmitFn || handleSubmit
                  }
                  disabled={
                    currentStep === 1 
                      ? !isCurrentStepValid()
                      : !bookingReady
                  }
                >
                  {currentStep === 1 ? (
                    <>
                      Next <i className="fas fa-arrow-right"></i>
                    </>
                  ) : (
                    <>
                      Book Sprinter Van <i className="fas fa-calendar-check"></i>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
      {/* Thank You Styles */}
      <style jsx>{`
        .nc-thank-you-container {
          min-height: 100vh;
          padding: 40px 20px;
          background: white;
        }

        .nc-thank-you-card {
          background: white;
          max-width: 1200px;
          width: 100%;
          margin: 0 auto;
          animation: thankYouSlideIn 0.6s ease-out;
        }

        @keyframes thankYouSlideIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .nc-thank-you-header {
          text-align: center;
          padding: 60px 20px 40px;
          border-bottom: 1px solid #e9ecef;
          margin-bottom: 40px;
        }

        .nc-thank-you-icon {
          font-size: 4rem;
          margin-bottom: 20px;
          color: #28a745;
          animation: checkBounce 0.8s ease-out 0.3s both;
        }

        @keyframes checkBounce {
          0% {
            transform: scale(0);
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
          }
        }

        .nc-thank-you-header h1 {
          font-size: 3rem;
          margin: 0 0 20px 0;
          font-weight: 600;
          color: #333;
        }

        .nc-thank-you-message {
          font-size: 1.2rem;
          line-height: 1.6;
          margin: 0;
          color: #555;
          max-width: 800px;
          margin: 0 auto;
        }

        .nc-thank-you-content {
          padding: 0;
          max-width: 1000px;
          margin: 0 auto;
        }

        .nc-appointment-confirmation,
        .nc-next-steps,
        .nc-contact-info {
          margin-bottom: 40px;
          padding: 30px;
          background: #fafafa;
          border: 1px solid #e9ecef;
          border-radius: 8px;
        }

        .nc-appointment-confirmation h3,
        .nc-next-steps h3,
        .nc-contact-info h3 {
          color: #333;
          margin: 0 0 20px 0;
          font-size: 1.3rem;
          font-weight: 500;
        }

        .nc-appointment-details {
          display: grid;
          gap: 12px;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
        }

        .nc-appointment-details > div {
          color: #555;
          font-size: 1rem;
        }

        .nc-steps-list {
          display: grid;
          gap: 16px;
        }

        .nc-step-item {
          display: flex;
          align-items: center;
          gap: 15px;
          color: #555;
          font-size: 1rem;
        }

        .nc-step-item i {
          color: #666;
          width: 20px;
          text-align: center;
        }

        .nc-contact-details {
          display: grid;
          gap: 10px;
          color: #555;
          font-size: 1rem;
        }

        .nc-thank-you-footer {
          text-align: center;
          padding: 40px 20px;
          border-top: 1px solid #e9ecef;
          margin-top: 40px;
        }

        .nc-thank-you-footer .nc-btn {
          background: #333;
          color: white;
          border: 1px solid #333;
          padding: 12px 24px;
          font-size: 1rem;
        }

        .nc-thank-you-footer .nc-btn:hover {
          background: #555;
          border-color: #555;
        }

        @media (max-width: 768px) {
          .nc-thank-you-container {
            padding: 20px 15px;
          }

          .nc-thank-you-header {
            padding: 40px 15px 30px;
          }

          .nc-thank-you-header h1 {
            font-size: 2.2rem;
          }

          .nc-thank-you-icon {
            font-size: 3rem;
          }

          .nc-thank-you-message {
            font-size: 1.1rem;
          }

          .nc-appointment-confirmation,
          .nc-next-steps,
          .nc-contact-info {
            padding: 20px;
            margin-bottom: 25px;
          }

          .nc-appointment-details {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default SprinterVanBookingForm;